import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Factory, Target, Brain, Banknote, ShieldCheck, Award } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { SEOHead, getOrganizationJsonLd } from '@/components/SEOHead'
import { PartnersSection } from '@/components/sections/PartnersSection'
import { ReviewsSection } from '@/components/sections/ReviewsSection'
import { productCategories } from '@/data/products'
import { sectors } from '@/data/sectors'
import type { SupportedLanguage } from '@/i18n'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
}

export function HomePage() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage

  return (
    <>
      <SEOHead
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        titleSuffix={false}
        ogImage="/media/homepage/telecamere-videosorveglianza-hero.png"
        jsonLd={getOrganizationJsonLd()}
      />

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster="/media/homepage/video-selea-poster.jpg"
            className="w-full h-full object-cover"
          >
            <source src="/media/homepage/video-selea.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-primary/60" />
        </div>

        <div className="container-main relative z-10 py-20">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-accent font-semibold text-sm uppercase tracking-wider mb-4"
            >
              Made in Italy — 25+ {t('home.yearsExperience')}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-5xl font-bold text-white leading-tight mb-6"
            >
              {t('home.heroTitle')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/80 mb-8 leading-relaxed"
            >
              {t('home.heroSubtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <LocalizedLink
                to={lang === 'en' ? '/products' : '/prodotti'}
                className="btn-primary"
              >
                {t('home.exploreProducts')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </LocalizedLink>
              <LocalizedLink
                to={lang === 'en' ? '/contacts' : '/contatti'}
                className="btn-outline border-white text-white hover:bg-white hover:text-primary"
              >
                {t('buttons.contactUs')}
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-3xl font-bold mb-4">{t('home.categoriesTitle')}</h2>
            <p className="text-text-muted max-w-2xl mx-auto">{t('home.categoriesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {productCategories.map((cat, index) => (
              <motion.div
                key={cat.id}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeInUp}
              >
                <LocalizedLink
                  to={`/${lang === 'en' ? 'products' : 'prodotti'}?filter=${cat.id}`}
                  className="group flex flex-col h-full card text-center p-8 hover:bg-primary transition-colors duration-normal"
                >
                  <div className="mb-6 flex justify-center">
                    <img
                      src={cat.icon}
                      alt={t(cat.nameKey)}
                      className="h-16 w-16 group-hover:hidden"
                      loading="lazy"
                    />
                    <img
                      src={cat.iconWhite}
                      alt={t(cat.nameKey)}
                      className="h-16 w-16 hidden group-hover:block"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-white transition-colors">
                    {t(cat.nameKey)}
                  </h3>
                  <p className="text-text-muted text-sm group-hover:text-white/70 transition-colors">
                    {t(cat.descriptionKey)}
                  </p>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="section-padding bg-surface-light">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-3xl font-bold mb-4">{t('home.sectorsTitle')}</h2>
            <p className="text-text-muted max-w-2xl mx-auto">{t('home.sectorsSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((sector, index) => (
              <motion.div
                key={sector.id}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeInUp}
              >
                <LocalizedLink
                  to={`/${lang === 'en' ? 'sectors' : 'settori'}/${sector.slug[lang]}`}
                  className="group relative block h-64 rounded-md overflow-hidden"
                >
                  <img
                    src={sector.heroImage}
                    alt={t(sector.nameKey)}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-slow"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white font-semibold text-lg">{t(sector.nameKey)}</h3>
                    <span className="text-white/70 text-sm flex items-center gap-1 mt-1 group-hover:text-accent transition-colors">
                      {t('buttons.learnMore')} <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </LocalizedLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="section-padding bg-primary">
        <div className="container-main">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-accent font-semibold text-sm uppercase tracking-wider mb-3"
            >
              {t('home.advantagesTagline')}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
            >
              {t('home.advantagesTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-white/60 max-w-2xl mx-auto text-lg"
            >
              {t('home.advantagesSubtitle')}
            </motion.p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Factory, key: 'home.advantage1' },
              { icon: Target, key: 'home.advantage2' },
              { icon: Brain, key: 'home.advantage3' },
              { icon: Banknote, key: 'home.advantage4' },
              { icon: ShieldCheck, key: 'home.advantage5' },
              { icon: Award, key: 'home.advantage6' },
            ].map((item, index) => (
              <motion.div
                key={item.key}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeInUp}
                className="bg-white/5 border border-white/10 rounded-xl p-7"
              >
                <item.icon className="w-5 h-5 text-accent/70 mb-3" />
                <div className="text-3xl font-bold text-accent mb-1">
                  {t(`${item.key}.stat`)}
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">
                  {t(`${item.key}.title`)}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {t(`${item.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <ReviewsSection />

      {/* Partners */}
      <PartnersSection />
    </>
  )
}
